import * as React from "react";
import { VeboxTreeView } from "../../../../../components/tree_view/VeboxTreeView";
import { VeboxModels } from "../../../../../types/veboxModels";
import {
	InputAddon,
	Spinner,
	SpinnerSize,
	TextInput,
	useDebounce,
} from "@vebox/etui-core";
import { AppTranslations } from "../../../../../components/Utils/translations/AppTranslations";
import { FindEquipmentContext } from "../../providers/FindEquipmentProvider";
import {
	INPUT_BORDER_BOX,
	Styles,
} from "../../../../../components/common/etui_components_styles";
import "./styles/styles.scss";
import * as S from "./styles/index";
import { TopHierarchyNode } from "./types";
import {
	buildHierarchiesTree,
	getHierarchyFilterInfo,
	isCommercialHierarchyNode,
	prepareHierarchyFilters,
} from "./utils";
import { ADDITIONAL_FILTERS } from "../../types";
import { Div, P } from "@vebox/etui-system-elements";
import Downshift from "downshift";
import {
	getAllSelectedNodes,
	hierarchyIncludesWord,
} from "../../../../../users/HierarchyVisibilityUtils";
import { HierarchySelectStatusCounter } from "../../components/HierarchySelectStatusCounter";

const INPUT_BORDER_BOX_WITH_MARGIN: Styles = {
	...INPUT_BORDER_BOX,
	root: {
		mb: 10,
	},
	input: {
		...INPUT_BORDER_BOX.input,
		borderRadius: 3,
	},
};

declare var AppTranslations: AppTranslations;

export const HierarchyTreeForFilters: React.FC = () => {
	const {
		hierarchyVisibleByTheUser,
		handleVeBoxAdditionalFilters,
		selectedAdditionalFilter,
		additionalVeBoxFilters,
		isRemovingAll,
	} = React.useContext(FindEquipmentContext);
	const { data, isLoading, isValidating, error } = hierarchyVisibleByTheUser;

	const [searchWord, setSearchWord] = React.useState("");
	const [searchWordDebounced, setSearchWordDebounced] = useDebounce(
		searchWord,
		150,
	);
	const [isFirstRender, setIsFirstRender] = React.useState(true);
	const [selectedHierarchy, setSelectedHierarchy] = React.useState<any[]>([]);
	const [hierarchiesTree, setHierarchiesTree] = React.useState<
		VeboxModels.HierarchiesTree[]
	>([]);

	const includeSomeNodes = React.useMemo(
		() =>
			hierarchiesTree.some(
				(tree) =>
					tree.label.includes(searchWord) ||
					(tree.children ?? []).filter(hierarchyIncludesWord(searchWord))
						.length > 0,
			),
		[searchWord, hierarchiesTree],
	);

	const stopPropagation: React.KeyboardEventHandler<any> = (e) => {
		e.stopPropagation();
	};

	const updateSearchWord = (e) => {
		setSearchWord(e.target.value);
		setSearchWordDebounced(e.target.value);
	};

	const changeHierarchy = (target) => {
		setIsFirstRender(false);
		setSelectedHierarchy(target);
	};

	const clearHierarchy = () => {
		setSelectedHierarchy([]);
	};

	const clearTextInput = () => {
		setSearchWord("");
		setSearchWordDebounced("");
	};

	const hierarchiesTreeFiltered = React.useMemo(() => {
		return hierarchiesTree.map((tree) =>
			buildHierarchiesTree(tree, searchWordDebounced),
		);
	}, [searchWordDebounced, hierarchiesTree]);

	const manageHierarchyFilter = (
		filterType: ADDITIONAL_FILTERS,
		hierarchy: TopHierarchyNode[],
		addOrReplace: boolean,
		isCommercialHierarchy: boolean,
	) => {
		const includeType = selectedAdditionalFilter.includes(filterType);
		if (isFirstRender) {
			return;
		}
		if (addOrReplace) {
			handleVeBoxAdditionalFilters(
				"add",
				prepareHierarchyFilters(hierarchy),
				filterType,
			);
		} else if (!addOrReplace && includeType) {
			handleVeBoxAdditionalFilters(
				"remove",
				isCommercialHierarchy
					? { saleFiltersCategory: additionalVeBoxFilters.saleFiltersCategory }
					: {
							companyFiltersCategory:
								additionalVeBoxFilters.companyFiltersCategory,
						},
				filterType,
			);
		}
	};

	const renderAutoSuggestInputAddon = () => {
		if (isLoading) {
			return (
				<InputAddon position="end">
					<Spinner size={SpinnerSize.xSmall} />
				</InputAddon>
			);
		}
		if (!isLoading && Boolean(searchWord)) {
			return (
				<InputAddon position="end">
					<span
						className="material-icons-outlined clickable"
						onClick={clearTextInput}
					>
						clear
					</span>
				</InputAddon>
			);
		}
		return null;
	};

	const selectedItemsText = React.useMemo(() => {
		return (selectedHierarchy?.length ?? 0) === 0
			? AppTranslations.massiveActions.massiveActionsCommon.selectedItem
			: AppTranslations.massiveActions.massiveActionsCommon.selectedItems;
	}, [selectedHierarchy]);

	React.useEffect(() => {
		const hierarchyNodeForFilter = getHierarchyFilterInfo(
			hierarchiesTree,
			selectedHierarchy ?? [],
		);

		const commercialHierarchy = hierarchyNodeForFilter.filter(
			isCommercialHierarchyNode,
		);
		manageHierarchyFilter(
			ADDITIONAL_FILTERS.COMMERCIAL_HIERARCHY,
			commercialHierarchy,
			commercialHierarchy.length > 0,
			true,
		);

		const otherHierarchies = hierarchyNodeForFilter.filter(
			(node) => !isCommercialHierarchyNode(node),
		);
		manageHierarchyFilter(
			ADDITIONAL_FILTERS.HIERARCHY,
			otherHierarchies,
			otherHierarchies.length > 0,
			false,
		);
	}, [selectedHierarchy, isFirstRender]);

	React.useEffect(() => {
		if (!isLoading && !isValidating && !error) {
			setHierarchiesTree(data?.list ?? []);
		}
	}, [isValidating, isLoading, error]);

	React.useEffect(() => {
		if (isRemovingAll) {
			clearHierarchy();
			clearTextInput();
		}
	}, [isRemovingAll]);

	return (
		<Div style={{ position: "relative" }}>
			<HierarchySelectStatusCounter
				title={""}
				count={getAllSelectedNodes(selectedHierarchy)?.length ?? 0}
				onDeselectAll={clearHierarchy}
				text={selectedItemsText}
			/>
			<Downshift>
				{({ getInputProps, getRootProps, isOpen, openMenu }) => {
					return (
						<Div position="relative" {...getRootProps({ refKey: "elRef" })}>
							<TextInput
								{...getInputProps()}
								label={AppTranslations.filters.equipment.company}
								value={searchWord}
								onFocus={() => openMenu()}
								onChange={updateSearchWord}
								onKeyPress={stopPropagation}
								autoComplete={"off"}
								styles={INPUT_BORDER_BOX_WITH_MARGIN as any}
								endInputAddon={renderAutoSuggestInputAddon()}
								disabled={isLoading}
							/>
							{isOpen && !isLoading ? (
								<S.SuggestionsContainer
									testId="suggestions-container"
									maxContainerHeight={"400px"}
								>
									<VeboxTreeView.CheckboxGroup
										onChange={changeHierarchy}
										values={selectedHierarchy ?? []}
										className={"sites-tree"}
									>
										{hierarchiesTreeFiltered}
									</VeboxTreeView.CheckboxGroup>
									{(!hierarchiesTreeFiltered.length || !includeSomeNodes) && (
										<P className={"empty-label"}>
											{AppTranslations.noDataAvailable}
										</P>
									)}
								</S.SuggestionsContainer>
							) : null}
						</Div>
					);
				}}
			</Downshift>
		</Div>
	);
};
